import { cloneElement, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowRight, Upload, Loader2, Check } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { trackBookAppointment } from "@/lib/ads";

const propertyTypes = ["House", "Condo", "Apartment", "Townhome", "Rental property", "Hotel", "Short-term rental", "Other"];

function bedroomsFor(type) {
  if (type === "Hotel" || type === "Short-term rental") return ["1 room", "2-5 rooms", "6-10 rooms", "10+ rooms"];
  return ["Studio", "1 bedroom", "2 bedrooms", "3 bedrooms", "4+ bedrooms"];
}

export default function BookingForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", property_type: "House", property_location: "", number_of_bedrooms: "", preferred_date: "", preferred_time: "Anytime", description: "" });
  const [photo, setPhoto] = useState(null);
  const [status, setStatus] = useState("idle");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      let photo_url = "";
      if (photo) {
        try {
          const { file_url } = await base44.integrations.Core.UploadFile({ file: photo });
          photo_url = file_url;
        } catch (_) {
          photo_url = "";
        }
      }
      const res = await base44.functions.invoke("submitBooking", { ...form, photo_url });
      if (res?.data?.ok) {
        trackBookAppointment(res?.data?.id);
        setStatus("done");
      } else setStatus("error");
    } catch (err) {
      setStatus("error");
    }
  };

  if (status === "done") {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center rounded-lg border border-border bg-background p-10 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/10 text-accent"><Check className="h-7 w-7" /></span>
        <h3 className="mt-6 font-heading text-2xl font-bold text-primary">Treatment request received.</h3>
        <p className="mt-3 max-w-md text-muted-foreground">We'll contact you to confirm availability and treatment requirements. Check your phone and email.</p>
        <Button onClick={() => setStatus("idle")} variant="outline" className="mt-6 h-11">Submit another request</Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-5 rounded-lg border border-border bg-background p-6 sm:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name *"><Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="text-black" /></Field>
        <Field label="Phone *"><Input required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="text-black" /></Field>
      </div>
      <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="text-black" /></Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Property type *">
          <Select value={form.property_type} onChange={(e) => { set("property_type", e.target.value); set("number_of_bedrooms", ""); }}>
            {propertyTypes.map((p) => <option key={p}>{p}</option>)}
          </Select>
        </Field>
        <Field label="Number of bedrooms">
          <Select value={form.number_of_bedrooms} onChange={(e) => set("number_of_bedrooms", e.target.value)}>
            <option value="">Select…</option>
            {bedroomsFor(form.property_type).map((b) => <option key={b}>{b}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="Property location *"><Input required placeholder="e.g. Vancouver, Burnaby…" value={form.property_location} onChange={(e) => set("property_location", e.target.value)} className="text-black" /></Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Preferred date"><Input type="date" value={form.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} className="text-black" /></Field>
        <Field label="Preferred time">
          <Select value={form.preferred_time} onChange={(e) => set("preferred_time", e.target.value)}>
            <option>Anytime</option><option>Morning</option><option>Afternoon</option>
          </Select>
        </Field>
      </div>
      <Field label="Brief description of the situation"><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="text-black" /></Field>
      <PhotoField photo={photo} setPhoto={setPhoto} />
      {status === "error" && <p className="text-sm text-accent">Something went wrong. Please try again or call us.</p>}
      <Button type="submit" disabled={status === "loading"} className="h-12 w-full bg-accent text-sm font-semibold tracking-wide text-white hover:bg-accent/90">
        {status === "loading" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</> : <>Book Bed Bug Treatment <ArrowRight className="ml-1 h-4 w-4" /></>}
      </Button>
    </form>
  );
}

function Field({ label, children }) {
  const id = useId();
  return (
    <div className="grid gap-2">
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
      {cloneElement(children, { id })}
    </div>
  );
}

function PhotoField({ photo, setPhoto }) {
  const id = useId();
  return (
    <div className="grid gap-2">
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Photo upload — optional</Label>
      <label htmlFor={id} className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-border px-4 py-3 text-sm text-muted-foreground hover:bg-muted/40">
        <Upload className="h-4 w-4" />
        {photo ? photo.name : "Attach a photo (optional)"}
        <input id={id} type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
      </label>
    </div>
  );
}

function Select({ value, onChange, children, ...rest }) {
  return (
    <select value={value} onChange={onChange} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-black shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" {...rest}>
      {children}
    </select>
  );
}